import { useEffect } from "react";
import api from "@/lib/api";
import { announcePageTitle } from "@/lib/googleTags";
import { getBrandName } from "@/context/BrandingContext";

function setMeta(name, content, property = false) {
  if (!content) return;
  const attr = property ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

// URL canonique de la page affichée. Absente jusqu'ici (audit du
// 09/09/2026), ce qui laissait Google traiter comme des pages distinctes la
// même page atteinte avec www, sans www, avec une ancre ou un paramètre de
// campagne. Posée ici et non en statique dans index.html : seule l'exécution
// connaît la route réellement affichée.
//
// Les paramètres de requête sont volontairement écartés — « ?lang=ar » et
// « ?utm_source=… » désignent la même page pour un moteur.
function setCanonical() {
  const href = window.location.origin + window.location.pathname;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function absolute(url) {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  return window.location.origin + url;
}

/**
 * `waitForData` : la page n'a pas encore les données qui composent son titre
 * (fiche produit en chargement). Rien n'est annoncé à Google tant qu'elles
 * ne sont pas là — sinon la vue de page partirait avec un titre provisoire.
 */
export function useSeo(key, overrides, { waitForData = false } = {}) {
  const ovr = JSON.stringify(overrides || {});
  useEffect(() => {
    let active = true;
    // Chemin de la page qui DEMANDE ce titre, figé maintenant : la réponse de
    // l'API peut arriver après une nouvelle navigation.
    const pathname = window.location.pathname;
    setCanonical();
    const apply = (seo) => {
      if (!active || waitForData) return;
      // Sans titre de référencement pour cette page, le nom de la boutique
      // plutôt que le titre de la page précédente, resté dans l'onglet.
      const title = seo?.title || getBrandName() || document.title;
      document.title = title;
      announcePageTitle(title, pathname);
      if (!seo) return;
      setMeta("description", seo.description);
      setMeta("keywords", seo.keywords);
      setMeta("og:title", seo.title, true);
      setMeta("og:description", seo.description, true);
      if (seo.og_image) setMeta("og:image", absolute(seo.og_image), true);
      setMeta("twitter:title", seo.title);
      setMeta("twitter:description", seo.description);
      if (seo.og_image) setMeta("twitter:image", absolute(seo.og_image));
    };
    api
      .get(`/seo/${key}`)
      .then(({ data }) => apply({ ...data, ...(overrides || {}) }))
      .catch(() => apply(overrides));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ovr, waitForData]);
}

/**
 * Titre d'une page SANS entrée de référencement (commande, confirmation,
 * compte) : posé sur l'onglet et annoncé à Google (page_title). `title` nul
 * tant qu'il n'est pas connu (nom de la boutique pas encore chargé).
 */
export function usePageTitle(title) {
  useEffect(() => {
    if (!title) return;
    document.title = title;
    announcePageTitle(title, window.location.pathname);
  }, [title]);
}
